import { AlertTriangle } from "@tamagui/lucide-icons";
import { useGlobalSearchParams } from "expo-router";
import { useContext, useEffect } from "react";
import { View, XStack, YStack } from "tamagui";

import { LayoutContext, ScrollView } from "@/components";
import ChatInput from "@/components/ChatInput";
import { H1, Paragraph } from "@/components/Typography";
import { GENERAL_EVENTS } from "@/config/adConversionEvents";
import { useAuthState } from "@/hooks/useAuthState";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import GeneralCareLandingPage from "@/landing-page/general-care/GeneralCareLandingPage";

const CURAI_ANNOUNCEMENT = {
  message: "Have a Curai subscription? Click here.",
  externalLinkHref: "https://chat.curaihealth.com",
};

export default function Index() {
  const router = usePropagatedRouter();
  const { conversationId, utm_source: utmSource } = useGlobalSearchParams<{
    conversationId?: string;
    utm_source?: string;
  }>();
  const { headerHeight } = useContext(LayoutContext);
  const { isLoading, isInitializing } = useAuthState();
  const isSignedIn = useIsSignedInStable();
  const isCuraiUtm = utmSource === "curaihealth.com";
  const isAuthLoading = isLoading || isInitializing;

  useEffect(() => {
    if (conversationId) {
      router.replace(`/conversation/${conversationId}`);
    }
  }, [conversationId, router]);

  if (isAuthLoading) {
    return <View flex={1} backgroundColor="$backgroundGround" />;
  }

  if (isSignedIn) {
    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: headerHeight.current,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        backgroundColor="$backgroundGround"
      >
        <YStack
          flex={1}
          alignItems="center"
          justifyContent="center"
          paddingHorizontal="$400"
          gap="$300"
        >
          <H1 textAlign="center">How can we help?</H1>
          <View width="100%" maxWidth={500}>
            <ChatInput
              key="chat-input-home"
              dataTrackingId="chat-input-home"
              extraTrackingContext={{ messageType: "home" }}
              placeholder="What can we help you with?"
            />
          </View>
        </YStack>
        <XStack
          paddingHorizontal="$400"
          paddingBottom="$300"
          justifyContent="center"
          alignItems="center"
          gap="$100"
        >
          <AlertTriangle color="$iconBase" size="$150" />
          <Paragraph size="$2xs" color="$textSecondary">
            Not a replacement for a doctor. For emergencies, call 911.
          </Paragraph>
        </XStack>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        paddingTop: headerHeight.current,
      }}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
      backgroundColor="$backgroundGround"
    >
      <GeneralCareLandingPage
        partnerConfig={{
          visitedSplashEventName: isCuraiUtm
            ? GENERAL_EVENTS.visitedSplashScreenWithCuraiUtm
            : GENERAL_EVENTS.visitedSplashScreen,
          ...(isCuraiUtm && { announcement: CURAI_ANNOUNCEMENT }),
        }}
      />
    </ScrollView>
  );
}
